import React, { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { Link, useParams } from 'wouter';
import { telemetry } from '../../../../@vendor/Posthog';
import { fuzzySearch, removeUndefined, useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../../concepts/resources/Icon';
import { Button, Colors, Input } from '../../../../ui';
import { PopupTabs } from '../../../../ui/PopupTabs';
import { useResolve } from '../../../Container';
import { AccountModel } from '../../../account/AccountModel';
import { usePermissionCheck } from '../../../common/PermissionElements';
import { Flexer, LinkButton } from '../../../dashboard/elements/LayoutElements';
import { Wibble } from '../../../workspace/Wibble';
import { SpotlightStrip } from '../SpotlightStrip';
import { useOnTemplateCreate, useTemplateData } from '../TemplateHooks';
import { TemplateSpotlightSingle } from '../TemplateSpotlightSingle';
import { StarredEmpty } from './StarredEmpty';
import { TeamEmpty } from './TeamEmpty';
import { TemplateListView } from './TemplateListView';

type PrimaryFilter = 'featured' | 'all' | 'team' | 'starred';
type SecondaryFilter = string;
type GalleryLayoutProps = { primary: PrimaryFilter, secondary: SecondaryFilter };


// {relevantTags?.length &&
//     <Flexer margin="0 .25">
//         <Wibble height="1.8" />
//     </Flexer>
// }

const useSearchViaQueryString = () => {
    const [local, setLocal] = useState('');
    const setValue = useCallback((next: string) => {
        window.history.replaceState({}, '', next ? `?q=${next}` : window.location.pathname);
        setLocal(next);
    }, []);
    const urlParams = new URLSearchParams(window.location.search);
    const value = local || urlParams.get('q') || '';
    return [value, setValue] as const;
};

const FeaturedLayout = ({ primary, secondary }: GalleryLayoutProps) => {

    const { templates, tags } = useTemplateData();
    const major = useMemo(
        () => templates?.filter(x => x.metadata.featured === 'major'),
        [templates]);
    const minor = useMemo(
        () => templates?.filter(x => x.metadata.featured === 'minor').sort((a, b) => (a.metadata.fsort || 99) - (b.metadata.fsort || 99)),
        [templates]);

    return <Flexer>
        <Flexer height="2" />
        {major?.map(x =>
            <TemplateSpotlightSingle key={x.id} templateId={x.id} />
        )}
        <Flexer height="4" />
        <SpotlightStrip>
            <TemplateListView center templates={minor} />
        </SpotlightStrip>
    </Flexer>;
};

const MainLayout = React.memo<GalleryLayoutProps>(({ primary, secondary }) => {

    const onCreate = useOnTemplateCreate();
    const { templates, tags } = useTemplateData();
    const selectedTag = useMemo(() => tags?.find(x => x.id === secondary), [tags, secondary]);
    // const [search, setSearch] = useState('');
    const [search, setSearch] = useSearchViaQueryString();
    const searchParam = !!search ? `?q=${search}` : '';

    const canCreate = usePermissionCheck('da:write', 'wo:write');

    useDebounce(() => search !== '' && telemetry.emit('template.search', { search }), 500);

    const primaryFilteredTemplates = useMemo(
        () => {
            const filtered = templates?.filter(x => {
                if (primary === 'team') return !!x.team;
                if (primary === 'starred') return x.starred;
                if (primary === 'all') return true;
                return true;
            });
            return (primary !== 'starred' && !!search.trim())
                ? fuzzySearch({
                    items: filtered,
                    keys: [{ name: 'searchableLabel', weight: 1 }, { name: 'description', weight: .5 }],
                    search,
                    threshold: 0.5,
                })
                : filtered;
        },
        [templates, primary, search]);

    const secondaryFilteredTemplates = useMemo(
        () => primaryFilteredTemplates?.filter(x => !secondary || !!x.tags.includes(selectedTag)),
        [primaryFilteredTemplates, secondary]);

    const relevantTags = useMemo(
        () => tags?.filter(x => x === selectedTag || primaryFilteredTemplates?.some(t => t.tags.includes(x))),
        [tags, primaryFilteredTemplates]);

    return <Flexer>
        <Flexer flex="row" align="start" gap="2">
            <Flexer flex="row" gap=".75" wrap="wrap" grow>
                {(primary === 'all') &&
                    <Flexer flex="row" gap=".75" align="center">
                        <Flexer width="16">
                            <Input autoFocus placeholder="Search templates" value={search} onChange={setSearch} />
                        </Flexer>
                        {!!relevantTags?.length &&
                            <Flexer margin="0 .25">
                                <Wibble height="1.8" />
                            </Flexer>
                        }
                    </Flexer>
                }
                {!!relevantTags?.length &&
                    <LinkButton to={`/templates/${primary}${searchParam}`} variant={!secondary ? 'primary' : 'common'}>
                        Everything
                    </LinkButton>
                }
                {relevantTags?.map(x =>
                    <LinkButton key={x.id} to={`/templates/${primary}/${x.id}${searchParam}`} variant={x.id === secondary ? 'primary' : 'common'}>
                        {x.label}
                    </LinkButton>
                )}
                {primary === 'team' && canCreate &&
                    <Flexer {...relevantTags?.length > 0 ? { padding: '0 0 0 1.5', margin: '0 0 0 1', borderLeft: `solid 1px ${Colors.border}` } : {}}>
                        <Button variant="positive" onAction={onCreate}>
                            <Icon src="deqo://icons/hero/plus" size="1" />&nbsp;Add Template
                        </Button>
                    </Flexer>
                }
            </Flexer>
            {/* <Flexer flex="row" center shrink>
                <Copy bold flush size=".875">Sort by:</Copy>
                <SortModeSelector options={sortOptions} value={sort} onChange={setSort} />
            </Flexer> */}
        </Flexer>
        <Flexer height="4" />
        {secondaryFilteredTemplates && <TemplateListView templates={secondaryFilteredTemplates} />}
        <Flexer flex="column" minHeight={20}>
            {primary === 'team' && !primaryFilteredTemplates?.length && <TeamEmpty />}
            {primary === 'starred' && !primaryFilteredTemplates?.length && <Flexer relative top="-4"><StarredEmpty /></Flexer>}
        </Flexer>
    </Flexer>;
});

export const TemplateGalleryView = () => {

    const account = useObserver(useResolve(AccountModel));
    const { primary, secondary } = {
        primary: 'featured',
        secondary: '',
        ...removeUndefined(useParams<{ primary: 'featured' | 'all' | 'team' | 'starred' }>())
    };

    return <Flexer>
        <PopupTabs border="none" spacing="1.5" size="lg">
            <Link to="/templates/featured">
                <PopupTabs.Item selected={primary === 'featured'}>
                    Featured
                </PopupTabs.Item>
            </Link>
            <Link to="/templates/all">
                <PopupTabs.Item selected={primary === 'all'}>
                    All Templates
                </PopupTabs.Item>
            </Link>
            <Link to="/templates/team">
                <PopupTabs.Item selected={primary === 'team'}>
                    {account.label} Templates
                </PopupTabs.Item>
            </Link>
            <Link to="/templates/starred">
                <PopupTabs.Item selected={primary === 'starred'}>
                    Starred Templates
                </PopupTabs.Item>
            </Link>
        </PopupTabs>
        <Flexer height="2" />
        {primary === 'featured' && <FeaturedLayout {...{ primary, secondary }} />}
        {primary !== 'featured' && <MainLayout {...{ primary, secondary }} />}
        <Flexer height="8" />
    </Flexer>;
};
